import { useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { useAddressRecord, useProjectRecord } from 'src/graphql';
import { mapClient } from 'src/graphql/entities/client';
import { EntityNames, QueryVariables } from 'src/graphql/types';
import { useSelectedConversation } from 'src/pages/Conversations';
import { GetClientsQuery, SEARCH_CLIENTS_QUERY } from './search-client';
import { SearchEntityReturnType } from './use-search';

/**
 * Custom hook to get a single record of different entities.
 *
 * Since React hooks cannot be conditionally rendered, all hooks are initialized and only the
 * selected entity's hook is active while others are disabled, this allows use to get a record of generic entity
 */
export const useSelectedRecord = ({
  recordId: rawRecordId,
  entityName,
}: {
  recordId?: string;
  entityName?: EntityNames;
}) => {
  const recordId = rawRecordId ?? '';

  const { selectedConversation: conversation, isLoading: isConversationLoading } =
    useSelectedConversation({
      conversationId: entityName === EntityNames.Conversation ? recordId : '',
    });

  const { address: site, isLoading: isSiteLoading } = useAddressRecord({
    addressId: entityName === EntityNames.Site ? recordId : '',
  });

  const clientId = entityName === EntityNames.Client ? recordId : '';

  const {
    data: clientData,
    previousData: prevClientData,
    loading: isClientLoading,
  } = useQuery<GetClientsQuery, QueryVariables>(SEARCH_CLIENTS_QUERY, {
    variables: {
      params: {
        filters: [{ attribute: 'clientId', operator: '=', value: clientId }],
        includeDeleted: true,
      },
    },
    skip: !clientId,
  });

  const client = useMemo(() => {
    const record = (clientData || prevClientData)?.getClient?.records?.find(
      (c) => c.clientId === clientId,
    );
    return clientId && record ? mapClient(record) : undefined;
  }, [clientData, prevClientData, clientId]);

  const { project, isLoading: isProjectLoading } = useProjectRecord({
    projectId: entityName === EntityNames.Project ? recordId : '',
  });

  const data = {
    [EntityNames.Conversation]: conversation,
    [EntityNames.Client]: client,
    [EntityNames.Site]: site,
    [EntityNames.Project]: project,
    [EntityNames.PlatformUser]: null,
  };

  const isLoading = {
    [EntityNames.Conversation]: isConversationLoading,
    [EntityNames.Client]: isClientLoading,
    [EntityNames.Site]: isSiteLoading,
    [EntityNames.Project]: isProjectLoading,
    [EntityNames.PlatformUser]: false,
  };

  if (!entityName) {
    return { selectedRecord: null, isLoading: false };
  }

  return {
    selectedRecord: (data[entityName] as SearchEntityReturnType[EntityNames]) ?? null,
    isLoading: Boolean(isLoading[entityName]),
  };
};
